import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || mobileMenuOpen ? 'bg-background-50/95 backdrop-blur-sm' : 'bg-transparent'}`}>
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="flex items-center gap-6 md:gap-8">
            <a href="/" className="flex items-center gap-2.5">
              <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-file-list-3-line text-lg text-background-50"></i>
              </div>
              <span className="font-heading font-semibold text-lg md:text-xl text-foreground-900 whitespace-nowrap">itaobuy sheet</span>
            </a>

            <div className="hidden md:flex items-center gap-1.5">
              <a
                href="/"
                className="px-3.5 py-2 rounded-full text-foreground-600 text-sm font-medium hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Home
              </a>
              <a
                href="/guida-itaobuy-spreadsheet"
                className="px-3.5 py-2 rounded-full text-foreground-600 text-sm font-medium hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Guida agli acquisti
              </a>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <div ref={dropdownRef} className="relative hidden md:block">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-300 bg-background-50 text-foreground-700 text-sm font-medium hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line text-base"></i>
                <span>Categorie</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-background-200 rounded-2xl shadow-lg overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-[420px] overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className={`${cat.icon} text-base`}></i>
                        </div>
                        <span>{cat.nameIt}</span>
                        <i className="ri-arrow-right-up-line text-xs text-foreground-400 ml-auto"></i>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>itaobuy sheet</span>
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-3 md:px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>Buy It Now</span>
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 z-40">
          <div
            className="absolute inset-0 bg-foreground-900/30 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          <div className="absolute top-0 right-0 w-[85%] max-w-[360px] h-[calc(100vh-64px)] bg-background-50 shadow-lg overflow-y-auto animate-[fadeIn_0.2s_ease-out]">
            <div className="p-4">
              <div className="flex flex-col gap-1 mb-4">
                <a
                  href="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 font-medium text-sm hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-home-line text-base"></i>
                  </div>
                  <span>Home</span>
                </a>
                <a
                  href="/guida-itaobuy-spreadsheet"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 font-medium text-sm hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-book-open-line text-base"></i>
                  </div>
                  <span>Guida agli acquisti</span>
                </a>
              </div>

              <div className="border-t border-background-200 pt-4">
                <h4 className="px-3 text-xs font-semibold text-foreground-400 uppercase tracking-widest mb-3">Categorie</h4>
                <div className="flex flex-col gap-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-primary-100">
                        <i className={`${cat.icon} text-base`}></i>
                      </div>
                      <span className="flex-1">{cat.nameIt}</span>
                      <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                    </a>
                  ))}
                </div>
              </div>

              <div className="border-t border-background-200 pt-4 mt-4">
                <h4 className="px-3 text-xs font-semibold text-foreground-400 uppercase tracking-widest mb-3">Guide SEO</h4>
                <div className="flex flex-col gap-0.5">
                  {[
                    { label: 'Guida Completa', href: '/guida-itaobuy-spreadsheet', icon: 'ri-guide-line' },
                    { label: 'Sicurezza Acquisti', href: '/itaobuy-spreadsheet-sicurezza', icon: 'ri-shield-check-line' },
                    { label: 'Tutte le Categorie', href: '/itaobuy-spreadsheet-categorie', icon: 'ri-grid-line' },
                    { label: 'Per Principianti', href: '/itaobuy-spreadsheet-principianti', icon: 'ri-user-star-line' },
                    { label: 'Migliori Prodotti', href: '/itaobuy-spreadsheet-migliori-prodotti', icon: 'ri-star-line' },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-secondary-50 hover:text-secondary-700 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                        <i className={`${link.icon} text-base`}></i>
                      </div>
                      <span>{link.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}